import { Loader2, Store } from 'lucide-react'
import { useStoreSettings } from '../hooks/useStoreSettings'
import { WooCommerceForm } from '../components/WooCommerceForm'
import { WidgetPreview } from '../components/WidgetPreview'
import { SyncButton } from '../components/SyncButton'

/**
 * Store Settings Page
 *
 * Configure WooCommerce connection, widget appearance, and trigger product sync
 */
export function StoreSettingsPage() {
  const { data: settings, isLoading, error } = useStoreSettings()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load settings</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Store className="h-6 w-6" />
          Store Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure your WooCommerce connection and search widget
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column: WooCommerce Connection */}
        <div className="space-y-6">
          <WooCommerceForm settings={settings} />
          <SyncButton settings={settings} />
        </div>

        {/* Right Column: Widget Config */}
        <div>
          <WidgetPreview settings={settings} />
        </div>
      </div>

      {/* Installation Instructions */}
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <h3 className="font-medium mb-2">Widget Installation</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Add the following code to your WooCommerce site's header or footer:
        </p>
        <pre className="bg-background rounded p-3 text-xs overflow-x-auto">
{`<!-- FlareSearch Widget -->
<script src="${window.location.origin}/widget.js"></script>
<script>
  FlareSearch.init({
    inputSelector: '.woocommerce-product-search input',
    mode: '${settings?.widgetMode || 'dropdown'}',
    maxResults: 10
  });
</script>`}
        </pre>
      </div>
    </div>
  )
}
